// HR Module TypeScript Types

export interface Tenant {
  id: string;
  name: string;
}

export enum Role {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  HR = 'HR',
  SALES = 'SALES',
  EMPLOYEE = 'EMPLOYEE'
}

export enum EmployeeStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ONBOARDING = 'ONBOARDING',
  OFFBOARDING = 'OFFBOARDING'
}

export enum LeaveType {
  ANNUAL = 'ANNUAL',
  CASUAL = 'CASUAL',
  MEDICAL = 'MEDICAL'
}

export enum LeaveUnit {
  DAY = 'DAY',
  HOUR = 'HOUR'
}

export enum LeaveStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED'
}

export enum CycleType {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY'
}

export enum KTType {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL'
}

export enum CompType {
  BASIC = 'BASIC',
  ALLOWANCE = 'ALLOWANCE',
  DEDUCTION = 'DEDUCTION',
  COMMISSION = 'COMMISSION',
  BONUS = 'BONUS'
}

export enum PayrollStatus {
  DRAFT = 'DRAFT',
  PROCESSING = 'PROCESSING',
  COMPLETE = 'COMPLETE'
}

export enum TaskStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  BLOCKED = 'BLOCKED'
}

export enum HolidayType {
  PUBLIC = 'PUBLIC',
  CUSTOM = 'CUSTOM'
}

export enum HolidayScope {
  ALL_COMPANY = 'ALL_COMPANY',
  SPECIFIC_TEAMS = 'SPECIFIC_TEAMS'
}

export interface Department {
  id: string;
  tenant_id: string;
  name: string;
  managerId?: string;
  parentId?: string;
  color?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Employee {
  id: string;
  tenant_id: string;
  first_name: string;
  last_name: string;
  job_title?: string;
  work_email?: string;
  work_phone?: string;
  email?: string;
  phone?: string;
  company?: string;
  department_id?: string;
  manager_id?: string;
  manager_name?: string;
  coach_id?: string;
  avatar_url?: string;
  private_info?: any;
  status: EmployeeStatus;
  // Personal Info
  date_of_birth?: string;
  gender?: string;
  marital_status?: string;
  nationality?: string;
  personal_email?: string;
  personal_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  // Work Info
  employee_number?: string;
  employment_type?: string;
  hire_date?: string;
  probation_end_date?: string;
  office_location?: string;
  work_schedule?: string;
  // Compensation
  base_salary?: number;
  allowances?: number;
  bank_name?: string;
  bank_account?: string;
  routing_number?: string;
  account_type?: string;
  // CRM Linked Data
  teams?: (string | EmployeeTeam)[];
  system_role?: string;
  role?: string;
  // Sub-modules
  skills?: EmployeeSkill[];
  leaves?: LeaveRequest[];
  reviews?: PerformanceReview[];
  salaryComponents?: SalaryComponent[];
  knowledgeTransfers?: KnowledgeTransfer[];
  onboardingTasks?: OnboardingTask[];
  offboardingTasks?: OffboardingTask[];
  created_at: Date;
  updated_at: Date;
}

export interface EmployeeTeam {
  id?: string;
  name: string;
  product_category?: string | null;
}

export interface SkillType {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  icon?: string; // Icon identifier for the category
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Skill {
  id: string;
  tenant_id: string;
  typeId: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface EmployeeSkill {
  id: string;
  tenant_id: string;
  employeeId: string;
  skillId: string;
  level: number; // 0-100
  notes?: string;
}

export interface LeavePolicy {
  id: string;
  tenant_id: string;
  name: string;
  leaveType: LeaveType;
  annualAllowance: number;
  unit: LeaveUnit;
}

export interface PublicHoliday {
  id: string;
  tenant_id: string;
  name: string;  // e.g., "New Year's Day" or "Company Picnic Day"
  date: Date;
  type: HolidayType;  // PUBLIC or CUSTOM
  countryCode?: string;  // ISO code (e.g., "US", "FR") - required for public holidays
  region?: string;  // Optional region specification
  scope: HolidayScope;  // ALL_COMPANY or SPECIFIC_TEAMS
  applicableTeams: string[];  // Array of department IDs, empty if scope is ALL_COMPANY
  isFullDay: boolean;  // True for full day, false for half day
  description?: string;  // Optional description
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;  // Employee ID who created the custom holiday
}

export interface LeaveRequest {
  id: string;
  tenant_id: string;
  employeeId: string;
  type: LeaveType;
  start: Date;
  end: Date;
  unit: LeaveUnit;
  reason?: string;
  status: LeaveStatus;
  approverId?: string;
  approverNotes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export enum ReviewType {
  CHECK_IN = 'CHECK_IN',
  PULSE_REVIEW = 'PULSE_REVIEW',
  FORMAL_REVIEW = 'FORMAL_REVIEW'
}

export enum ReviewStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  OVERDUE = 'OVERDUE'
}

export enum GoalType {
  STANDARD = 'STANDARD',
  OKR = 'OKR',
  KPI = 'KPI',
  KNOWLEDGE_TRANSFER = 'KNOWLEDGE_TRANSFER',
  DEVELOPMENT = 'DEVELOPMENT'
}

export enum GoalStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  ON_TRACK = 'ON_TRACK',
  AT_RISK = 'AT_RISK',
  BEHIND = 'BEHIND',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum FeedbackType {
  MANAGER = 'MANAGER',
  PEER = 'PEER',
  SELF = 'SELF',
  DIRECT_REPORT = 'DIRECT_REPORT',
  AI_GENERATED = 'AI_GENERATED',
  STAKEHOLDER = 'STAKEHOLDER'
}

export enum FeedbackStatus {
  REQUESTED = 'REQUESTED',
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  OVERDUE = 'OVERDUE'
}

export enum RecognitionType {
  PEER_BADGE = 'PEER_BADGE',
  SPOT_AWARD = 'SPOT_AWARD',
  ACHIEVEMENT = 'ACHIEVEMENT',
  MILESTONE = 'MILESTONE'
}

export enum PerformanceRating {
  EXCEEDS = 'EXCEEDS',
  MEETS = 'MEETS',
  PARTIALLY_MEETS = 'PARTIALLY_MEETS',
  DOES_NOT_MEET = 'DOES_NOT_MEET'
}

// Performance Review Interfaces
export interface PerformanceReviewCycle {
  id: string;
  tenant_id: string;
  name: string;
  type: ReviewType;
  startDate: Date;
  endDate: Date;
  isActive: boolean;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface PerformanceReview {
  id: string;
  tenant_id: string;
  employeeId: string;
  reviewerId: string;
  cycleId: string;
  type: ReviewType;
  status: ReviewStatus;
  overallRating?: PerformanceRating;
  goalAttainmentScore?: number; // 0-100
  competencyRatings: CompetencyRating[];
  managerFeedback?: string;
  employeeSelfAssessment?: string;
  developmentPlan?: string;
  nextReviewDate?: Date;
  aiInsights?: AIInsight[];
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date;
}

export interface CompetencyRating {
  id: string;
  competencyName: string; // e.g., "Communication", "Code Quality"
  rating: PerformanceRating;
  feedback?: string;
  aiGenerated: boolean;
  weight?: number; // for weighted scoring
}

export interface AIInsight {
  id: string;
  type: 'FEEDBACK' | 'BIAS_DETECTION' | 'SENTIMENT' | 'SKILL_GAP' | 'RECOMMENDATION';
  title: string;
  content: string;
  confidence: number; // 0-1
  actionRequired: boolean;
  createdAt: Date;
}

// Goal Management Interfaces
export interface PerformanceGoal {
  id: string;
  tenant_id: string;
  employeeId: string;
  managerId: string;
  type: GoalType;
  title: string;
  description: string;
  successCriteria: string;
  targetDate: Date;
  status: GoalStatus;
  progress: number; // 0-100
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  linkedSkills: string[]; // skill IDs from Skills Library
  knowledgeTransferInfo?: KnowledgeTransferInfo;
  milestones: GoalMilestone[];
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date;
}

export interface KnowledgeTransferInfo {
  expertId?: string; // Employee who will provide the knowledge
  courseUrl?: string;
  materials: string[];
  estimatedHours: number;
  completionCertificate?: string;
}

export interface GoalMilestone {
  id: string;
  title: string;
  description?: string;
  targetDate: Date;
  status: GoalStatus;
  completedAt?: Date;
}

// 360-Degree Feedback Interfaces
export interface FeedbackRequest {
  id: string;
  tenant_id: string;
  requesterId: string; // Employee requesting feedback
  providerId: string; // Person who will provide feedback
  reviewId?: string; // Associated performance review
  type: FeedbackType;
  status: FeedbackStatus;
  questions: FeedbackQuestion[];
  response?: FeedbackResponse;
  dueDate: Date;
  sentAt: Date;
  respondedAt?: Date;
}

export interface FeedbackQuestion {
  id: string;
  question: string;
  category: string; // e.g., "Communication", "Leadership"
  required: boolean;
}

export interface FeedbackResponse {
  id: string;
  answers: FeedbackAnswer[];
  overallRating?: PerformanceRating;
  additionalComments?: string;
  anonymous: boolean;
}

export interface FeedbackAnswer {
  questionId: string;
  answer: string;
  rating?: number; // 1-5 scale
}

// Recognition & Rewards Interfaces
export interface Recognition {
  id: string;
  tenant_id: string;
  giverId: string;
  receiverId: string;
  type: RecognitionType;
  title: string;
  description: string;
  badgeType?: string; // e.g., "Team Player", "Innovator"
  points?: number;
  monetaryValue?: number;
  isPublic: boolean;
  createdAt: Date;
}

export interface PerformanceMetrics {
  id: string;
  tenant_id: string;
  employeeId: string;
  period: string; // e.g., "2024-Q1"
  goalCompletionRate: number; // 0-100
  feedbackScore: number; // average rating from all feedback
  recognitionCount: number;
  skillsGrowth: SkillGrowthMetric[];
  performanceTrend: 'IMPROVING' | 'STABLE' | 'DECLINING';
  lastUpdated: Date;
}

export interface SkillGrowthMetric {
  skillId: string;
  skillName: string;
  previousLevel: number;
  currentLevel: number;
  improvement: number;
  targetLevel: number;
}

export interface KnowledgeTransfer {
  id: string;
  tenant_id: string;
  fromEmployeeId: string;
  toEmployeeId: string;
  type: KTType;
  title: string;
  description?: string;
  dueDate?: Date;
  status: TaskStatus;
}

export interface SalaryComponent {
  id: string;
  tenant_id: string;
  employeeId: string;
  type: CompType;
  name: string;
  amount: number;
  currency: string;
  effective: Date;
}

export interface CommissionRule {
  id: string;
  tenant_id: string;
  name: string;
  criteria: string;
  rate: number;
  cap?: number;
}

export interface PayrollRun {
  id: string;
  tenant_id: string;
  title: string;
  fromDate: Date;
  toDate: Date;
  status: PayrollStatus;
  totalAmount: number;
  currency: string;
}

export interface SalarySlip {
  id: string;
  tenant_id: string;
  employeeId: string;
  payrollRunId: string;
  totalEarnings: number;
  totalDeductions: number;
  netPay: number;
  currency: string;
}

export interface OnboardingTask {
  id: string;
  tenant_id: string;
  employeeId: string;
  title: string;
  description?: string;
  dueDate?: Date;
  status: TaskStatus;
  assignedTo?: string;
}

export interface OffboardingTask {
  id: string;
  tenant_id: string;
  employeeId: string;
  title: string;
  description?: string;
  dueDate?: Date;
  status: TaskStatus;
  assignedTo?: string;
}

// ===== PAYROLL MODULE TYPES =====

export enum PaySchedule {
  WEEKLY = 'WEEKLY',
  BI_WEEKLY = 'BI_WEEKLY',
  SEMI_MONTHLY = 'SEMI_MONTHLY',
  MONTHLY = 'MONTHLY'
}

export enum PayType {
  SALARY = 'SALARY',
  HOURLY = 'HOURLY',
  COMMISSION = 'COMMISSION',
  CONTRACT = 'CONTRACT'
}

export enum PayrollRunStatus {
  DRAFT = 'DRAFT',
  READY = 'READY',
  PROCESSING = 'PROCESSING',
  REVIEW = 'REVIEW',
  APPROVED = 'APPROVED',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED'
}

export enum TaxStatus {
  SINGLE = 'SINGLE',
  MARRIED_JOINTLY = 'MARRIED_JOINTLY',
  MARRIED_SEPARATELY = 'MARRIED_SEPARATELY',
  HEAD_OF_HOUSEHOLD = 'HEAD_OF_HOUSEHOLD'
}

export enum PaymentMethod {
  DIRECT_DEPOSIT = 'DIRECT_DEPOSIT',
  CHECK = 'CHECK',
  CASH = 'CASH'
}

export enum DeductionType {
  TAX = 'TAX',
  INSURANCE = 'INSURANCE',
  RETIREMENT = 'RETIREMENT',
  OTHER = 'OTHER'
}

export enum BenefitType {
  HEALTH = 'HEALTH',
  DENTAL = 'DENTAL',
  VISION = 'VISION',
  LIFE = 'LIFE',
  DISABILITY = 'DISABILITY',
  RETIREMENT = 'RETIREMENT'
}

export enum OvertimeType {
  NONE = 'NONE',
  STANDARD = 'STANDARD', // 1.5x after 40 hours
  CALIFORNIA = 'CALIFORNIA', // 1.5x after 8 hours daily, 2x after 12 hours
  CUSTOM = 'CUSTOM'
}

export enum SalesRole {
  INSIDE_SALES = 'INSIDE_SALES',
  OUTSIDE_SALES = 'OUTSIDE_SALES',
  ACCOUNT_MANAGER = 'ACCOUNT_MANAGER',
  SALES_MANAGER = 'SALES_MANAGER',
  BDR = 'BDR',
  SDR = 'SDR'
}

// Employee Payroll Profile
export interface PayrollProfile {
  id: string;
  tenant_id: string;
  employeeId: string;
  payType: PayType;
  paySchedule: PaySchedule;
  baseSalary?: number; // Annual for salary, hourly for hourly
  hourlyRate?: number;
  currency: string;
  taxStatus: TaxStatus;
  taxAllowances: number;
  paymentMethod: PaymentMethod;
  bankAccount?: BankAccount;
  overtimeType: OvertimeType;
  overtimeRates: OvertimeRate[];
  salesRole?: SalesRole;
  commissionStructure?: CommissionStructure;
  isActive: boolean;
  effectiveDate: Date;
  endDate?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface BankAccount {
  routingNumber: string;
  accountNumber: string;
  accountType: 'CHECKING' | 'SAVINGS';
  bankName?: string;
}

export interface OvertimeRate {
  id: string;
  type: 'DAILY' | 'WEEKLY' | 'HOLIDAY';
  threshold: number; // hours
  multiplier: number; // e.g., 1.5, 2.0
  description?: string;
}

// Commission and Sales Compensation
export interface CommissionStructure {
  id: string;
  name: string;
  baseRate: number; // percentage
  tiers: CommissionTier[];
  cap?: number; // maximum commission amount
  floor?: number; // minimum commission amount
  recoverable: boolean; // draw against future commissions
  payoutSchedule: PaySchedule;
}

export interface CommissionTier {
  id: string;
  name: string;
  threshold: number; // sales amount threshold
  rate: number; // commission percentage
  accelerator?: number; // additional rate above threshold
}

export interface SalesTransaction {
  id: string;
  tenant_id: string;
  employeeId: string;
  amount: number;
  currency: string;
  date: Date;
  type: 'SALE' | 'CHARGEBACK' | 'ADJUSTMENT';
  description?: string;
  commissionEligible: boolean;
  commissionAmount?: number;
  payrollRunId?: string;
}

// Benefits and Deductions
export interface Benefit {
  id: string;
  tenant_id: string;
  employeeId: string;
  type: BenefitType;
  name: string;
  provider?: string;
  employeeContribution: number; // amount or percentage
  employerContribution: number;
  isPercentage: boolean;
  maxAmount?: number;
  effectiveDate: Date;
  endDate?: Date;
  isActive: boolean;
}

export interface Deduction {
  id: string;
  tenant_id: string;
  employeeId: string;
  type: DeductionType;
  name: string;
  amount: number;
  isPercentage: boolean;
  maxAmount?: number;
  minAmount?: number;
  isPreTax: boolean;
  effectiveDate: Date;
  endDate?: Date;
  isActive: boolean;
}

// Tax Configuration
export interface TaxConfiguration {
  id: string;
  tenant_id: string;
  country: string;
  region?: string; // state/province
  taxYear: number;
  federalTaxRates: TaxBracket[];
  stateTaxRates: TaxBracket[];
  localTaxRates: TaxBracket[];
  socialSecurityRate: number;
  medicareRate: number;
  unemploymentRate: number;
  disabilityRate?: number;
  otherRates: TaxRate[];
  isActive: boolean;
}

export interface TaxBracket {
  id: string;
  minIncome: number;
  maxIncome?: number;
  rate: number;
  flatAmount?: number;
}

export interface TaxRate {
  id: string;
  name: string;
  rate: number;
  cap?: number;
  appliesTo: 'EMPLOYEE' | 'EMPLOYER' | 'BOTH';
}

// Payroll Run and Processing
export interface PayrollRun {
  id: string;
  tenant_id: string;
  title: string;
  payPeriodStart: Date;
  payPeriodEnd: Date;
  payDate: Date;
  schedule: PaySchedule;
  status: PayrollRunStatus;
  totalGrossPay: number;
  totalTaxes: number;
  totalDeductions: number;
  totalNetPay: number;
  employeeCount: number;
  currency: string;
  approvedBy?: string;
  approvedAt?: Date;
  processedBy?: string;
  processedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface PayrollCalculation {
  id: string;
  tenant_id: string;
  payrollRunId: string;
  employeeId: string;
  regularHours: number;
  overtimeHours: number;
  holidayHours: number;
  vacationHours: number;
  sickHours: number;
  regularPay: number;
  overtimePay: number;
  holidayPay: number;
  vacationPay: number;
  sickPay: number;
  commissionPay: number;
  bonusPay: number;
  grossPay: number;
  federalTax: number;
  stateTax: number;
  localTax: number;
  socialSecurityTax: number;
  medicareTax: number;
  unemploymentTax: number;
  totalTaxes: number;
  benefitDeductions: number;
  otherDeductions: number;
  totalDeductions: number;
  netPay: number;
  currency: string;
  leaveUsed: LeaveUsage[];
}

export interface LeaveUsage {
  leaveType: LeaveType;
  hoursUsed: number;
  daysUsed: number;
  isPaid: boolean;
}

export interface Payslip {
  id: string;
  tenant_id: string;
  employeeId: string;
  payrollRunId: string;
  payPeriodStart: Date;
  payPeriodEnd: Date;
  payDate: Date;
  calculation: PayrollCalculation;
  issuedAt: Date;
  downloadUrl?: string;
}

// Time and Attendance Integration
export interface TimeEntry {
  id: string;
  tenant_id: string;
  employeeId: string;
  date: Date;
  clockIn: Date;
  clockOut?: Date;
  breakDuration?: number; // minutes
  regularHours: number;
  overtimeHours: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  notes?: string;
  approvedBy?: string;
  approvedAt?: Date;
}

// Self-Service Features
export interface PayrollDocument {
  id: string;
  tenant_id: string;
  employeeId: string;
  type: 'W2' | 'W4' | 'PAYSTUB' | 'TAX_FORM' | 'BENEFITS_SUMMARY';
  title: string;
  description?: string;
  year?: number;
  fileUrl: string;
  uploadedAt: Date;
}

export interface TaxWithholding {
  id: string;
  tenant_id: string;
  employeeId: string;
  federalAllowances: number;
  stateAllowances: number;
  additionalFederal: number;
  additionalState: number;
  isExemptFederal: boolean;
  isExemptState: boolean;
  effectiveDate: Date;
  w4Date?: Date;
}

export interface PayrollAlert {
  id: string;
  tenant_id: string;
  type: 'ERROR' | 'WARNING' | 'INFO';
  title: string;
  message: string;
  employeeId?: string;
  payrollRunId?: string;
  isResolved: boolean;
  createdAt: Date;
  resolvedAt?: Date;
  resolvedBy?: string;
}

// Dashboard and Reporting
export interface PayrollSummary {
  id: string;
  tenant_id: string;
  period: string;
  totalEmployees: number;
  totalGrossPay: number;
  totalTaxes: number;
  totalBenefits: number;
  totalNetPay: number;
  avgGrossPay: number;
  avgNetPay: number;
  currency: string;
  generatedAt: Date;
}

export interface PayrollReport {
  id: string;
  tenant_id: string;
  name: string;
  type: 'PAYROLL_REGISTER' | 'TAX_LIABILITY' | 'BENEFITS_REPORT' | 'COMMISSION_REPORT' | 'OVERTIME_REPORT';
  filters: any;
  data: any;
  generatedAt: Date;
  generatedBy: string;
}

// Approval Workflows
export interface PayrollApproval {
  id: string;
  tenant_id: string;
  payrollRunId: string;
  approverId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  comments?: string;
  requestedAt: Date;
  respondedAt?: Date;
}

// ===== COMPREHENSIVE HR SETTINGS MODULE TYPES =====

// 1. System & General Settings
export enum NotificationType {
  EMAIL = 'EMAIL',
  SYSTEM = 'SYSTEM',
  SMS = 'SMS',
  PUSH = 'PUSH'
}

export enum FieldType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  DROPDOWN = 'DROPDOWN',
  CHECKBOX = 'CHECKBOX',
  TEXTAREA = 'TEXTAREA',
  FILE = 'FILE'
}

export enum AccessLevel {
  READ = 'READ',
  WRITE = 'WRITE',
  DELETE = 'DELETE',
  ADMIN = 'ADMIN'
}

export enum WorkflowStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  DRAFT = 'DRAFT'
}

export interface CompanySettings {
  id: string;
  tenant_id: string;
  companyName: string;
  legalEntityName: string;
  logoUrl?: string;
  address: Address;
  financialYearStart: number; // month (1-12)
  defaultCurrency: string;
  defaultTimezone: string;
  themeSettings: ThemeSettings;
  createdAt: Date;
  updatedAt: Date;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface ThemeSettings {
  primaryColor: string;
  secondaryColor: string;
  logoPlacement: 'left' | 'center' | 'right';
  darkMode: boolean;
}

export interface NotificationTemplate {
  id: string;
  tenant_id: string;
  name: string;
  type: NotificationType;
  subject: string;
  body: string;
  triggers: string[];
  isActive: boolean;
  variables: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface SystemSecurity {
  id: string;
  tenant_id: string;
  passwordPolicy: PasswordPolicy;
  ipRestrictions: string[];
  auditLogging: boolean;
  dataRetentionDays: number;
  twoFactorAuth: boolean;
  sessionTimeout: number;
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSymbols: boolean;
  expiryDays: number;
}

export interface IntegrationSettings {
  id: string;
  tenant_id: string;
  apiKeys: ApiKey[];
  externalIntegrations: ExternalIntegration[];
}

export interface ApiKey {
  id: string;
  name: string;
  service: string;
  keyValue: string;
  isActive: boolean;
  expiresAt?: Date;
}

export interface ExternalIntegration {
  id: string;
  name: string;
  type: 'PAYROLL' | 'ATTENDANCE' | 'RECRUITMENT' | 'LEARNING' | 'OTHER';
  endpoint: string;
  authMethod: string;
  isActive: boolean;
  lastSync?: Date;
}

// 2. Core HR & Employee Master Data Configuration
export interface OrganizationStructure {
  id: string;
  tenant_id: string;
  departments: Department[];
  costCenters: CostCenter[];
  locations: Location[];
  businessUnits: BusinessUnit[];
}

export interface CostCenter {
  id: string;
  code: string;
  name: string;
  description?: string;
  managerId?: string;
  isActive: boolean;
}

export interface Location {
  id: string;
  name: string;
  code: string;
  address: Address;
  timezone: string;
  workingHours: WorkingHours;
  isActive: boolean;
}

export interface BusinessUnit {
  id: string;
  name: string;
  code: string;
  headId?: string;
  costCenterId?: string;
  isActive: boolean;
}

export interface JobConfiguration {
  id: string;
  tenant_id: string;
  jobTitles: JobTitle[];
  grades: Grade[];
  payScales: PayScale[];
}

export interface JobTitle {
  id: string;
  title: string;
  description: string;
  departmentId: string;
  gradeId?: string;
  isActive: boolean;
}

export interface Grade {
  id: string;
  name: string;
  level: number;
  minSalary: number;
  maxSalary: number;
  currency: string;
}

export interface PayScale {
  id: string;
  gradeId: string;
  step: number;
  amount: number;
  effectiveDate: Date;
}

export interface CustomField {
  id: string;
  tenant_id: string;
  name: string;
  label: string;
  type: FieldType;
  section: string; // e.g., 'basic_info', 'personal_info'
  options?: string[]; // for dropdown
  isRequired: boolean;
  isActive: boolean;
  order: number;
  validation?: FieldValidation;
}

export interface FieldValidation {
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  minValue?: number;
  maxValue?: number;
}

export interface UserRole {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  permissions: Permission[];
  isSystemRole: boolean;
  isActive: boolean;
}

export interface Permission {
  id: string;
  module: string;
  resource: string;
  actions: AccessLevel[];
}

// 3. Workforce Management & Leave Settings
export interface WorkforceSettings {
  id: string;
  tenant_id: string;
  workSchedules: WorkSchedule[];
  attendanceSettings: AttendanceSettings;
  leaveSettings: LeaveSettings;
}

export interface WorkSchedule {
  id: string;
  name: string;
  workingHours: WorkingHours;
  workingDays: number[]; // 0-6, Sunday-Saturday
  isDefault: boolean;
  shifts: Shift[];
}

export interface WorkingHours {
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  breakStart: string;
  breakEnd: string;
  totalHours: number;
}

export interface Shift {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  restTime: number; // minutes
  scanRange: number; // minutes before/after allowed
}

export interface AttendanceSettings {
  clockInMethods: string[];
  geoFencing: GeoFencing;
  biometricIntegration: boolean;
  lateMarkPolicy: LateMarkPolicy;
  overtimeRules: OvertimeRule[];
}

export interface GeoFencing {
  enabled: boolean;
  radius: number; // meters
  locations: GeofenceLocation[];
}

export interface GeofenceLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  radius: number;
}

export interface LateMarkPolicy {
  gracePeriod: number; // minutes
  deductionRules: DeductionRule[];
  notificationEnabled: boolean;
}

export interface DeductionRule {
  id: string;
  lateMinutes: number;
  deductionType: 'FIXED' | 'HOURLY' | 'DAILY';
  deductionAmount: number;
}

export interface OvertimeRule {
  id: string;
  name: string;
  threshold: number; // hours
  multiplier: number;
  applicableRoles: string[];
  requiresApproval: boolean;
}

export interface LeaveSettings {
  leaveTypes: LeaveTypeConfig[];
  approvalWorkflow: ApprovalWorkflow;
  carryOverRules: CarryOverRule[];
  holidayCalendars: HolidayCalendar[];
}

export interface LeaveTypeConfig {
  id: string;
  name: string;
  code: string;
  maxDaysPerYear: number;
  accrualRate: number; // days per month
  requiresApproval: boolean;
  allowHalfDay: boolean;
  canCarryOver: boolean;
  maxCarryOver: number;
  applicableAfter: number; // months of employment
}

export interface ApprovalWorkflow {
  id: string;
  name: string;
  levels: ApprovalLevel[];
  autoApprovalRules?: AutoApprovalRule[];
  escalationRules?: EscalationRule[];
}

export interface ApprovalLevel {
  level: number;
  approverRole: string;
  isMandatory: boolean;
  timeoutDays?: number;
}

export interface AutoApprovalRule {
  id: string;
  condition: string;
  maxDays: number;
  leaveTypes: string[];
}

export interface EscalationRule {
  id: string;
  afterDays: number;
  escalateTo: string;
  notificationTemplate: string;
}

export interface CarryOverRule {
  id: string;
  leaveTypeId: string;
  maxDays: number;
  expiryMonths: number;
  requiresApproval: boolean;
}

export interface HolidayCalendar {
  id: string;
  name: string;
  country: string;
  region?: string;
  holidays: Holiday[];
  isDefault: boolean;
}

export interface Holiday {
  id: string;
  name: string;
  date: Date;
  isOptional: boolean;
  applicableLocations: string[];
}

// 4. Payroll & Compensation Settings
export interface PayrollSettings {
  id: string;
  tenant_id: string;
  salaryComponents: SalaryComponentConfig[];
  payrollProcessing: PayrollProcessingConfig;
  statutoryCompliance: StatutoryCompliance;
}

export interface SalaryComponentConfig {
  id: string;
  name: string;
  code: string;
  type: CompType;
  isStatutory: boolean;
  calculationFormula: string;
  isActive: boolean;
  applicableGrades: string[];
}

export interface PayrollProcessingConfig {
  payCycle: PaySchedule;
  bufferSize: number;
  loggingLevel: 'INFO' | 'DEBUG' | 'ERROR';
  autoProcessing: boolean;
  payslipTemplate: string;
  deliveryMethod: 'EMAIL' | 'PORTAL' | 'BOTH';
}

export interface StatutoryCompliance {
  id: string;
  country: string;
  pfSettings: PFSettings;
  taxSettings: TaxSettings;
  insuranceSettings: InsuranceSettings;
}

export interface PFSettings {
  employeeContribution: number;
  employerContribution: number;
  maxSalaryLimit: number;
  isActive: boolean;
}

export interface TaxSettings {
  taxSlabs: TaxSlab[];
  standardDeduction: number;
  declarationPeriod: string;
}

export interface TaxSlab {
  id: string;
  minAmount: number;
  maxAmount?: number;
  taxRate: number;
  surcharge?: number;
}

export interface InsuranceSettings {
  esicRate: number;
  maxSalaryLimit: number;
  isActive: boolean;
}

// 5. Performance & Talent Management Settings
export interface PerformanceSettings {
  id: string;
  tenant_id: string;
  reviewCycles: ReviewCycleConfig[];
  goalSettings: GoalSettings;
  competencyFramework: CompetencyFramework;
  feedbackSettings: FeedbackSettings;
}

export interface ReviewCycleConfig {
  id: string;
  name: string;
  frequency: 'ANNUAL' | 'SEMI_ANNUAL' | 'QUARTERLY' | 'MONTHLY';
  participants: ReviewParticipant[];
  templates: ReviewTemplate[];
  isActive: boolean;
}

export interface ReviewParticipant {
  role: 'SELF' | 'MANAGER' | 'PEER' | 'SKIP_LEVEL' | 'SUBORDINATE';
  isRequired: boolean;
  permissions: string[];
}

export interface ReviewTemplate {
  id: string;
  name: string;
  sections: ReviewSection[];
  ratingScale: RatingScale;
}

export interface ReviewSection {
  id: string;
  title: string;
  weight: number;
  questions: ReviewQuestion[];
}

export interface ReviewQuestion {
  id: string;
  question: string;
  type: 'TEXT' | 'RATING' | 'MULTIPLE_CHOICE';
  isRequired: boolean;
  options?: string[];
}

export interface RatingScale {
  id: string;
  name: string;
  scale: ScalePoint[];
  type: 'NUMERIC' | 'DESCRIPTIVE';
}

export interface ScalePoint {
  value: number;
  label: string;
  description?: string;
}

export interface GoalSettings {
  goalTypes: GoalTypeConfig[];
  trackingMethods: string[];
  cascadingRules: CascadingRule[];
  weightingSystem: boolean;
}

export interface GoalTypeConfig {
  id: string;
  name: string;
  requiresMetrics: boolean;
  allowsSubGoals: boolean;
  defaultWeight: number;
}

export interface CascadingRule {
  id: string;
  fromLevel: string;
  toLevel: string;
  isAutomatic: boolean;
  percentage: number;
}

export interface CompetencyFramework {
  id: string;
  name: string;
  competencies: Competency[];
  proficiencyLevels: ProficiencyLevel[];
}

export interface Competency {
  id: string;
  name: string;
  description: string;
  category: string;
  behaviors: string[];
  applicableRoles: string[];
}

export interface ProficiencyLevel {
  id: string;
  level: number;
  name: string;
  description: string;
}

export interface FeedbackSettings {
  enable360Feedback: boolean;
  allowAnonymousFeedback: boolean;
  feedbackFrequency: string;
  reminderSettings: ReminderSettings;
}

export interface ReminderSettings {
  enabled: boolean;
  frequency: number; // days
  escalationDays: number;
}

// 6. Recruitment & Onboarding Settings
export interface RecruitmentSettings {
  id: string;
  tenant_id: string;
  recruitmentPipeline: RecruitmentStage[];
  integrations: RecruitmentIntegration[];
  templates: DocumentTemplate[];
  onboardingSettings: OnboardingSettings;
}

export interface RecruitmentStage {
  id: string;
  name: string;
  order: number;
  isRequired: boolean;
  autoAdvance: boolean;
  participants: string[];
  timeLimit?: number; // days
}

export interface RecruitmentIntegration {
  id: string;
  platform: string;
  isActive: boolean;
  autoSync: boolean;
  apiCredentials: string;
}

export interface DocumentTemplate {
  id: string;
  name: string;
  type: 'OFFER_LETTER' | 'APPOINTMENT_LETTER' | 'INCREMENT_LETTER' | 'EXPERIENCE_LETTER';
  template: string;
  variables: TemplateVariable[];
  isActive: boolean;
}

export interface TemplateVariable {
  name: string;
  type: string;
  source: string; // employee field or calculation
}

export interface OnboardingSettings {
  id: string;
  checklists: OnboardingChecklist[];
  autoAssignment: AutoAssignmentRule[];
  documentCollection: DocumentCollectionRule[];
}

export interface OnboardingChecklist {
  id: string;
  name: string;
  department?: string;
  role?: string;
  tasks: OnboardingTask[];
}

export interface OnboardingTask {
  id: string;
  name: string;
  description: string;
  assignedTo: 'HR' | 'MANAGER' | 'IT' | 'FINANCE' | 'EMPLOYEE';
  dueAfterDays: number;
  isRequired: boolean;
  dependencies: string[];
}

export interface AutoAssignmentRule {
  id: string;
  condition: string;
  assignTo: string;
  taskType: string;
}

export interface DocumentCollectionRule {
  id: string;
  documentType: string;
  isRequired: boolean;
  collectBefore: number; // days before joining
  reminderDays: number[];
}

export interface ExitSettings {
  id: string;
  clearanceWorkflow: ClearanceWorkflow;
  noticePeriods: NoticePeriod[];
  settlementRules: SettlementRule[];
}

export interface ClearanceWorkflow {
  id: string;
  name: string;
  clearances: Clearance[];
}

export interface Clearance {
  id: string;
  department: string;
  responsible: string;
  isRequired: boolean;
  documents: string[];
}

export interface NoticePeriod {
  id: string;
  roleLevel: string;
  days: number;
  canWaive: boolean;
}

export interface SettlementRule {
  id: string;
  component: string;
  calculationMethod: string;
  includeInFFS: boolean;
}

// Shift Planning Module Types
export enum ShiftPlanningStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED'
}

export enum ShiftType {
  REGULAR = 'REGULAR',
  OVERTIME = 'OVERTIME',
  NIGHT_SHIFT = 'NIGHT_SHIFT',
  WEEKEND = 'WEEKEND',
  HOLIDAY = 'HOLIDAY',
  ON_CALL = 'ON_CALL'
}

export enum RotationType {
  WEEKLY = 'WEEKLY',
  BI_WEEKLY = 'BI_WEEKLY',
  MONTHLY = 'MONTHLY',
  FIXED = 'FIXED',
  CUSTOM = 'CUSTOM'
}

export enum ShiftAssignmentStatus {
  ASSIGNED = 'ASSIGNED',
  REQUESTED = 'REQUESTED',
  SWAPPED = 'SWAPPED',
  CANCELLED = 'CANCELLED',
  TEMPORARY = 'TEMPORARY'
}

export interface ShiftPlanningSettings {
  id: string;
  tenant_id: string;
  shiftDefinitions: ShiftDefinition[];
  schedulingRules: SchedulingRule[];
  rotationPatterns: RotationPattern[];
  swapSettings: ShiftSwapSettings;
  complianceRules: ComplianceRule[];
  notifications: ShiftNotificationSettings;
}

export interface ShiftDefinition {
  id: string;
  name: string;
  code: string;
  type: ShiftType;
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
  duration: number; // hours
  breakTime: number; // minutes
  color: string;
  isActive: boolean;
  locations: string[];
  departments: string[];
  minStaffRequired: number;
  maxStaffAllowed: number;
  overtimeThreshold?: number;
  rateMultiplier: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface SchedulingRule {
  id: string;
  name: string;
  type: 'MINIMUM_REST' | 'MAXIMUM_CONSECUTIVE' | 'WEEKLY_HOURS' | 'MONTHLY_HOURS';
  value: number;
  unit: 'HOURS' | 'DAYS' | 'SHIFTS';
  isActive: boolean;
  applicableRoles: string[];
  priority: number;
}

export interface RotationPattern {
  id: string;
  name: string;
  type: RotationType;
  pattern: string[]; // Array of shift IDs
  duration: number; // in days/weeks
  restDays: number[];
  isActive: boolean;
  description?: string;
}

export interface ShiftSwapSettings {
  allowSwaps: boolean;
  requireManagerApproval: boolean;
  advanceNoticeDays: number;
  cutoffHours: number;
  allowBulkSwaps: boolean;
}

export interface ComplianceRule {
  id: string;
  name: string;
  type: 'LABOR_LAW' | 'COMPANY_POLICY' | 'UNION_AGREEMENT';
  rule: string;
  isActive: boolean;
  penalty?: string;
  exemptRoles?: string[];
}

export interface ShiftNotificationSettings {
  schedulePublish: NotificationConfig;
  shiftReminder: NotificationConfig;
  swapRequest: NotificationConfig;
  overtime: NotificationConfig;
}

export interface NotificationConfig {
  enabled: boolean;
  method: 'EMAIL' | 'SMS' | 'PUSH' | 'ALL';
  advanceDays?: number;
  advanceHours?: number;
  template: string;
}

export interface ShiftSchedule {
  id: string;
  tenant_id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  status: ShiftPlanningStatus;
  shifts: ScheduledShift[];
  createdBy: string;
  createdAt: Date;
  publishedAt?: Date;
  approvedBy?: string;
  approvedAt?: Date;
}

export interface ScheduledShift {
  id: string;
  scheduleId: string;
  shiftDefinitionId: string;
  employeeId?: string;
  date: Date;
  status: ShiftAssignmentStatus;
  assignedBy: string;
  assignedAt: Date;
  swapRequestId?: string;
  notes?: string;
  checkInTime?: Date;
  checkOutTime?: Date;
  actualHours?: number;
  overtimeHours?: number;
}

export interface ShiftSwapRequest {
  id: string;
  requesterEmployeeId: string;
  targetEmployeeId?: string;
  originalShiftId: string;
  proposedShiftId?: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  requestedAt: Date;
  respondedAt?: Date;
  respondedBy?: string;
  response?: string;
}

export interface ShiftTemplate {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  rotationPatternId: string;
  validFrom: Date;
  validTo?: Date;
  isActive: boolean;
  assignments: TemplateAssignment[];
}

export interface TemplateAssignment {
  employeeId: string;
  position: number;
  isFixed: boolean;
  effectiveFrom: Date;
  effectiveTo?: Date;
}

export interface ShiftReport {
  id: string;
  type: 'ATTENDANCE' | 'OVERTIME' | 'COVERAGE' | 'COMPLIANCE';
  period: {
    startDate: Date;
    endDate: Date;
  };
  data: ShiftReportData;
  generatedAt: Date;
}

export interface ShiftReportData {
  totalShifts: number;
  coveredShifts: number;
  uncoveredShifts: number;
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  employeeStats: EmployeeShiftStats[];
  complianceViolations: ComplianceViolation[];
}

export interface EmployeeShiftStats {
  employeeId: string;
  employeeName: string;
  totalShifts: number;
  workedShifts: number;
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  absences: number;
  lateArrivals: number;
  earlyDepartures: number;
}

export interface ComplianceViolation {
  ruleId: string;
  ruleName: string;
  employeeId: string;
  date: Date;
  description: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH';
}

// Onboarding & Offboarding Enhanced Types
export interface OnboardingProcess {
  id: string;
  employeeId: string;
  tenant_id: string;
  startDate: Date;
  expectedCompletionDate: Date;
  actualCompletionDate?: Date;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'DELAYED';
  currentStage: string;
  completionPercentage: number;
  checklistId: string;
  assignedTo: string;
  buddy?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface OffboardingProcess {
  id: string;
  employeeId: string;
  tenant_id: string;
  initiatedBy: string;
  reason: 'RESIGNATION' | 'TERMINATION' | 'RETIREMENT' | 'CONTRACT_END';
  lastWorkingDay: Date;
  status: 'INITIATED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  currentStage: string;
  completionPercentage: number;
  clearanceId: string;
  exitInterviewCompleted: boolean;
  exitInterviewDate?: Date;
  finalSettlement?: FinalSettlement;
  handoverNotes?: string;
  accessRevoked: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface OnboardingTaskProgress {
  id: string;
  processId: string;
  taskId: string;
  status: TaskStatus;
  assignedTo: string;
  dueDate: Date;
  completedDate?: Date;
  completedBy?: string;
  notes?: string;
  attachments?: string[];
  feedback?: string;
}

export interface OffboardingTaskProgress {
  id: string;
  processId: string;
  clearanceId: string;
  department: string;
  responsible: string;
  status: TaskStatus;
  dueDate: Date;
  completedDate?: Date;
  completedBy?: string;
  documents: string[];
  notes?: string;
  feedback?: string;
}

export interface FinalSettlement {
  id: string;
  processId: string;
  basicSalary: number;
  pendingLeaves: number;
  bonuses: number;
  deductions: number;
  grossAmount: number;
  taxDeductions: number;
  netAmount: number;
  processedBy: string;
  processedDate: Date;
  paymentDate?: Date;
  paymentMethod: PaymentMethod;
  status: 'CALCULATED' | 'APPROVED' | 'PAID';
}

export interface BuddyProgram {
  id: string;
  tenant_id: string;
  onboardeeId: string;
  buddyId: string;
  assignedDate: Date;
  duration: number; // days
  status: 'ACTIVE' | 'COMPLETED' | 'TERMINATED';
  meetingSchedule: BuddyMeeting[];
  feedback?: BuddyFeedback;
}

export interface BuddyMeeting {
  id: string;
  buddyProgramId: string;
  scheduledDate: Date;
  actualDate?: Date;
  duration: number; // minutes
  shiftDefinitionId: string;
  employeeId?: string;
  date: Date;
  status: ShiftAssignmentStatus;
  assignedBy: string;
  assignedAt: Date;
  swapRequestId?: string;
  notes?: string;
  checkInTime?: Date;
  checkOutTime?: Date;
  actualHours?: number;
  overtimeHours?: number;
}

export interface ShiftSwapRequest {
  id: string;
  requesterEmployeeId: string;
  targetEmployeeId?: string;
  originalShiftId: string;
  proposedShiftId?: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  requestedAt: Date;
  respondedAt?: Date;
  respondedBy?: string;
  response?: string;
}

export interface ShiftTemplate {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  rotationPatternId: string;
  validFrom: Date;
  validTo?: Date;
  isActive: boolean;
  assignments: TemplateAssignment[];
}

export interface TemplateAssignment {
  employeeId: string;
  position: number;
  isFixed: boolean;
  effectiveFrom: Date;
  effectiveTo?: Date;
}

export interface ShiftReport {
  id: string;
  type: 'ATTENDANCE' | 'OVERTIME' | 'COVERAGE' | 'COMPLIANCE';
  period: {
    startDate: Date;
    endDate: Date;
  };
  data: ShiftReportData;
  generatedAt: Date;
}

export interface ShiftReportData {
  totalShifts: number;
  coveredShifts: number;
  uncoveredShifts: number;
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  employeeStats: EmployeeShiftStats[];
  complianceViolations: ComplianceViolation[];
}

export interface EmployeeShiftStats {
  employeeId: string;
  employeeName: string;
  totalShifts: number;
  workedShifts: number;
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  absences: number;
  lateArrivals: number;
  earlyDepartures: number;
}

export interface ComplianceViolation {
  ruleId: string;
  ruleName: string;
  employeeId: string;
  date: Date;
  description: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH';
}

// Onboarding & Offboarding Enhanced Types
export interface OnboardingProcess {
  id: string;
  employeeId: string;
  tenant_id: string;
  startDate: Date;
  expectedCompletionDate: Date;
  actualCompletionDate?: Date;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'DELAYED';
  currentStage: string;
  completionPercentage: number;
  checklistId: string;
  assignedTo: string;
  buddy?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface OffboardingProcess {
  id: string;
  employeeId: string;
  tenant_id: string;
  initiatedBy: string;
  reason: 'RESIGNATION' | 'TERMINATION' | 'RETIREMENT' | 'CONTRACT_END';
  lastWorkingDay: Date;
  status: 'INITIATED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  currentStage: string;
  completionPercentage: number;
  clearanceId: string;
  exitInterviewCompleted: boolean;
  exitInterviewDate?: Date;
  finalSettlement?: FinalSettlement;
  handoverNotes?: string;
  accessRevoked: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface OnboardingTaskProgress {
  id: string;
  processId: string;
  taskId: string;
  status: TaskStatus;
  assignedTo: string;
  dueDate: Date;
  completedDate?: Date;
  completedBy?: string;
  notes?: string;
  attachments?: string[];
  feedback?: string;
}

export interface OffboardingTaskProgress {
  id: string;
  processId: string;
  clearanceId: string;
  department: string;
  responsible: string;
  status: TaskStatus;
  dueDate: Date;
  completedDate?: Date;
  completedBy?: string;
  documents: string[];
  notes?: string;
  feedback?: string;
}

export interface FinalSettlement {
  id: string;
  processId: string;
  basicSalary: number;
  pendingLeaves: number;
  bonuses: number;
  deductions: number;
  grossAmount: number;
  taxDeductions: number;
  netAmount: number;
  processedBy: string;
  processedDate: Date;
  paymentDate?: Date;
  paymentMethod: PaymentMethod;
  status: 'CALCULATED' | 'APPROVED' | 'PAID';
}

export interface BuddyProgram {
  id: string;
  tenant_id: string;
  onboardeeId: string;
  buddyId: string;
  assignedDate: Date;
  duration: number; // days
  status: 'ACTIVE' | 'COMPLETED' | 'TERMINATED';
  meetingSchedule: BuddyMeeting[];
  feedback?: BuddyFeedback;
}

export interface BuddyMeeting {
  id: string;
  buddyProgramId: string;
  scheduledDate: Date;
  actualDate?: Date;
  duration: number; // minutes
  topics: string[];
  notes?: string;
  completed: boolean;
}

export interface BuddyFeedback {
  id: string;
  buddyProgramId: string;
  rating: number;
  feedback: string;
  improvements: string[];
  submittedBy: 'BUDDY' | 'ONBOARDEE';
  submittedAt: Date;
}

// ===== DASHBOARD TYPES =====

export interface DashboardStats {
  total_employees: number;
  active_employees: number;
  total_departments: number;
  pending_approvals: number;
  diversity: {
    female_percentage: number;
    male_percentage: number;
    female_count: number;
    male_count: number;
  };
  shortfall_employees: number;
  performance_review_percentage: number;
  upcoming_exit_interviews: number;
  pending_letter_requests: number;
  to_be_joined: number;
  in_notice_period: number;
  overhead_ratio: number;
}

export interface DepartmentComparisonData {
  dept: string;
  department_id: string;
  y2024: number;
  current: number;
}

export interface DepartmentComparison {
  data: DepartmentComparisonData[];
  current_year: number;
  comparison_year: number;
}